import { useState, useEffect, useCallback, useMemo } from "react";
import type {
  ExposureBandEntry,
  ExposureBandsMap,
} from "../adapters/echarts/types";

const STORAGE_PREFIX = "playground_exposure_bands";

export function getExposureBandsStorageKey(
  userId: string,
  exposureType: string
): string {
  return `${STORAGE_PREFIX}:${userId}:${exposureType}`;
}

export function isBandEntryValid(entry: ExposureBandEntry | undefined): boolean {
  if (!entry) return false;
  const { min, max, target } = entry;
  if (
    min === undefined ||
    max === undefined ||
    target === undefined ||
    Number.isNaN(min) ||
    Number.isNaN(max) ||
    Number.isNaN(target)
  ) {
    return false;
  }
  return min <= max && min <= target && target <= max;
}

export function validateExposureBands(
  categories: string[],
  bands: ExposureBandsMap
): { valid: boolean; error?: string } {
  if (categories.length === 0) {
    return { valid: false, error: "No categories available." };
  }

  for (const category of categories) {
    const entry = bands[category];
    if (!entry) {
      return {
        valid: false,
        error: `All categories must have min, max, and target values. Missing: ${category}`,
      };
    }

    const min = Number(entry.min);
    const max = Number(entry.max);
    const target = Number(entry.target);

    if (
      entry.min === undefined ||
      entry.max === undefined ||
      entry.target === undefined ||
      entry.min === null ||
      entry.max === null ||
      entry.target === null ||
      String(entry.min).trim() === "" ||
      String(entry.max).trim() === "" ||
      String(entry.target).trim() === "" ||
      Number.isNaN(min) ||
      Number.isNaN(max) ||
      Number.isNaN(target)
    ) {
      return {
        valid: false,
        error: `All categories must have min, max, and target values. Incomplete: ${category}`,
      };
    }

    if (min > max) {
      return {
        valid: false,
        error: `Min must be less than or equal to max for ${category}.`,
      };
    }

    if (target < min || target > max) {
      return {
        valid: false,
        error: `Target must be between min and max for ${category}.`,
      };
    }
  }

  return { valid: true };
}

export function isBandsCompleteForCategories(
  categories: string[],
  bands: ExposureBandsMap | null | undefined
): boolean {
  if (!bands || categories.length === 0) return false;
  return validateExposureBands(categories, bands).valid;
}

export function useExposureBands(
  userId: string | undefined,
  exposureType: string
) {
  const [bands, setBands] = useState<ExposureBandsMap | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const storageKey = useMemo(() => {
    if (!userId) return null;
    return getExposureBandsStorageKey(userId, exposureType);
  }, [userId, exposureType]);

  useEffect(() => {
    if (!storageKey) {
      setBands(null);
      setIsLoaded(true);
      return;
    }

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        setBands(JSON.parse(stored) as ExposureBandsMap);
      } else {
        setBands(null);
      }
    } catch (error) {
      console.error("Error loading exposure bands:", error);
      setBands(null);
    } finally {
      setIsLoaded(true);
    }
  }, [storageKey]);

  const saveBands = useCallback(
    (
      categories: string[],
      nextBands: ExposureBandsMap
    ): { success: boolean; error?: string } => {
      const validation = validateExposureBands(categories, nextBands);
      if (!validation.valid) {
        return { success: false, error: validation.error };
      }

      if (!storageKey) {
        return { success: false, error: "User not authenticated." };
      }

      try {
        localStorage.setItem(storageKey, JSON.stringify(nextBands));
        setBands(nextBands);
        return { success: true };
      } catch (error) {
        console.error("Error saving exposure bands:", error);
        return { success: false, error: "Failed to save bands." };
      }
    },
    [storageKey]
  );

  const clearBands = useCallback(() => {
    if (!storageKey) return;
    try {
      localStorage.removeItem(storageKey);
      setBands(null);
    } catch (error) {
      console.error("Error clearing exposure bands:", error);
    }
  }, [storageKey]);

  const isCompleteForCategories = useCallback(
    (categories: string[]) => isBandsCompleteForCategories(categories, bands),
    [bands]
  );

  return {
    bands,
    isLoaded,
    saveBands,
    clearBands,
    isCompleteForCategories,
  };
}
